import React, { useState } from 'react';

import {
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputLabel,
    Stack,
    TextField,
    Tooltip
} from '@mui/material';
import { DeleteFilled } from '@ant-design/icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    unitedStates,
    errorMessage,
    canadianProvinces,
    plateRegExp,
    universalNameRegExp,
    onlyNumeric
} from 'utils/constants';
import InputFieldErrorMessage from '../common/InputFieldErrorMessage';
import { MODAL_ACTION_TYPE } from '../../utils/constants/modal';
import IconButton from '../@extended/IconButton';
import Select from '../common/Select';
import Autocomplete from '../common/Autocomplete';
import capitalize from '../../utils/capitalize';
import { Loader } from '../index';

const CreateUpdateVehicleModal = ({
    vehicle,
    users,
    modalType,
    onSubmit,
    onDelete,
    onClose
}) => {
    const [submitting, setSubmitting] = useState(false);

    const countries = [
        { value: 'USA', name: 'United States' },
        { value: 'CAN', name: 'Canada' }
    ];

    const initialValues = {
        country: undefined,
        ownerId: undefined,
        tag: undefined,
        make: undefined,
        model: undefined,
        state: undefined,
        year: undefined,
        color: undefined,
        ...vehicle
    };

    const userOptions = users.map(user => ({
        key: user.uid,
        value: capitalize(`${user.first_name} ${user.last_name}`)
    }));

    const validationSchema = Yup.object().shape({
        ownerId: Yup.string()
            .test('check-users', errorMessage.vehicle.owner.notExists, function () {
                if (!users || users.length === 0) {
                    return this.createError({
                        message: errorMessage.vehicle.owner.notExists
                    });
                }
                return true;
            })
            .when([], {
                is: () => users && users.length > 0,
                then: schema => schema.required(errorMessage.vehicle.owner.required)
            }),
        tag: Yup.string()
            .trim()
            .required(errorMessage.vehicle.tag.required)
            .matches(plateRegExp, 'Plate is not valid'),
        make: Yup.string()
            .required(errorMessage.vehicle.make.required)
            .matches(universalNameRegExp, 'Make is not valid'),
        model: Yup.string()
            .required(errorMessage.vehicle.model.required)
            .matches(universalNameRegExp, 'Model is not valid'),
        country: Yup.string().required(errorMessage.vehicle.country.required),
        state: Yup.string().required(errorMessage.vehicle.provinceOrState.required),
        year: Yup.string().matches(onlyNumeric, 'Year is not valid').nullable(),
        color: Yup.string().matches(universalNameRegExp, 'Color is not valid').nullable()
    });

    const handleSubmit = async values => {
        setSubmitting(true);
        try {
            await onSubmit({ ...values, tag: values.tag.toUpperCase() }, onClose);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = () => {
        onDelete(vehicle, onClose);
    };

    return (
        <>
            {submitting && <Loader />}
            <DialogTitle>
                {modalType === MODAL_ACTION_TYPE.EDIT
                    ? 'Edit Vehicle'
                    : 'Add New Vehicle'}
            </DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                validateOnChange
                validateOnBlur
                validateOnMount
                initialTouched={(!users || users.length === 0) && { ownerId: true }}
                initialErrors={
                    (!users || users.length === 0) && {
                        ownerId: errorMessage.vehicle.owner.notExists
                    }
                }
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                    touched,
                    errors,
                    values
                }) => (
                    <>
                        <DialogContent sx={{ p: 2.5 }}>
                            <Grid container columnSpacing={2}>
                                <Grid item xs={12}>
                                    <Stack>
                                        <InputLabel htmlFor={'ownerId'}>Owner</InputLabel>
                                        <Autocomplete
                                            id="ownerId"
                                            options={userOptions}
                                            getOptionLabel={opt => opt.value || ''}
                                            isOptionEqualToValue={(opt, val) =>
                                                opt.key === val.key
                                            }
                                            renderOption={(props, opt) => (
                                                <li {...props} key={opt.key}>
                                                    {opt.value}
                                                </li>
                                            )}
                                            disableCloseOnSelect={false}
                                            value={
                                                userOptions.find(
                                                    opt => opt.key === values.ownerId
                                                ) || null
                                            }
                                            onChange={entry => {
                                                setFieldValue(
                                                    'ownerId',
                                                    entry ? entry.key : ''
                                                );
                                            }}
                                            disabled={!users || users.length === 0}
                                            error={touched.ownerId && !!errors.ownerId}
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.ownerId}
                                            error={errors.ownerId}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack>
                                        <InputLabel htmlFor={'make'}>Make</InputLabel>
                                        <TextField
                                            name="make"
                                            required
                                            placeholder="Make"
                                            value={values.make}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.make && Boolean(errors.make)}
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.make}
                                            error={errors.make}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack>
                                        <InputLabel htmlFor={'model'}>Model</InputLabel>
                                        <TextField
                                            name="model"
                                            required
                                            placeholder="Model"
                                            value={values.model}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.model && Boolean(errors.model)}
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.model}
                                            error={errors.model}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Stack>
                                        <InputLabel htmlFor={'tag'}>Plate #</InputLabel>
                                        <TextField
                                            name="tag"
                                            id={'tagInput'}
                                            required
                                            placeholder="Plate/Tag #"
                                            value={values.tag}
                                            inputProps={{
                                                style: { textTransform: 'uppercase' }
                                            }}
                                            onChange={e =>
                                                setFieldValue(
                                                    'tag',
                                                    e.target.value.toUpperCase()
                                                )
                                            }
                                            onBlur={handleBlur}
                                            error={touched.tag && Boolean(errors.tag)}
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.tag}
                                            error={errors.tag}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Stack>
                                        <InputLabel htmlFor={'country'}>
                                            Country
                                        </InputLabel>
                                        <Select
                                            label="Country"
                                            name="country"
                                            value={values.country}
                                            onChange={e => {
                                                handleChange(e);
                                                if (e.target.name === 'country') {
                                                    setFieldValue('state', null);
                                                    setFieldTouched('state', true); // So that validation error shows instantly
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            touched={touched.country}
                                            error={errors.country}
                                            required
                                            options={countries.map(loc => ({
                                                key: loc.value,
                                                value: loc.name
                                            }))}
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.country}
                                            error={errors.country}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Stack>
                                        <InputLabel htmlFor={'state'}>
                                            {values.country === 'CAN'
                                                ? 'Province'
                                                : 'State'}
                                        </InputLabel>
                                        <Select
                                            label={
                                                values.country === 'CAN'
                                                    ? 'Province'
                                                    : 'State'
                                            }
                                            name="state"
                                            value={values.state}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            touched={touched.state}
                                            error={errors.state}
                                            options={(values.country === 'CAN'
                                                ? canadianProvinces
                                                : unitedStates
                                            ).map(loc => ({
                                                key: loc.value,
                                                value: loc.name
                                            }))}
                                            required
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.state}
                                            error={errors.state}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack>
                                        <InputLabel htmlFor={'year'}>Year</InputLabel>
                                        <TextField
                                            name="year"
                                            placeholder="Year"
                                            value={values.year}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.year && Boolean(errors.year)}
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.year}
                                            error={errors.year}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack>
                                        <InputLabel htmlFor={'color'}>Color</InputLabel>
                                        <TextField
                                            name="color"
                                            placeholder="Color"
                                            value={values.color}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.color && Boolean(errors.color)}
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.color}
                                            error={errors.color}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2.5 }}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    {modalType === MODAL_ACTION_TYPE.EDIT && (
                                        <Tooltip title="Delete Vehicle" placement="top">
                                            <IconButton
                                                size="large"
                                                color="error"
                                                onClick={handleDelete}
                                                disabled={submitting}
                                            >
                                                <DeleteFilled />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Grid>
                                <Grid item>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Button
                                            color="error"
                                            onClick={onClose}
                                            disabled={submitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={
                                                submitting ||
                                                !users ||
                                                users.length === 0
                                            }
                                            onClick={handleSubmit}
                                        >
                                            {submitting ? (
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >
                                                    <CircularProgress
                                                        size={18}
                                                        color="inherit"
                                                    />
                                                    <span>
                                                        {modalType ===
                                                        MODAL_ACTION_TYPE.EDIT
                                                            ? 'Updating...'
                                                            : 'Saving...'}
                                                    </span>
                                                </Stack>
                                            ) : modalType ===
                                              MODAL_ACTION_TYPE.EDIT ? (
                                                'Update'
                                            ) : (
                                                'Save'
                                            )}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </>
                )}
            </Formik>
        </>
    );
};

export default CreateUpdateVehicleModal;
